import { useEffect, useRef, useState } from 'react';

/**
 * Global map storing shared IntersectionObserver instances for memory optimization.
 * Key format: "${rootMargin}-${threshold}-${root ? 'custom' : 'viewport'}"
 * Reduces memory footprint by reusing observers with identical configurations.
 * @type {Map<string, IntersectionObserver>}
 */
const observersMap = new Map();
const getObserverKey = (rootMargin, threshold, root) => {
  return `${rootMargin}-${threshold}-${root ? 'custom' : 'viewport'}`;
};

/**
 * Gets or creates a shared IntersectionObserver instance with the specified configuration.
 * Implements the singleton pattern to minimize memory usage across multiple elements.
 *
 * @param {string} rootMargin - Margin around the root element for intersection calculation
 * @param {number} threshold - Percentage of element visibility required to trigger intersection
 * @param {Element|null} root - Root element for intersection observation (null = viewport)
 * @returns {IntersectionObserver} Shared observer instance with callback map
 */
const getObserver = (rootMargin, threshold, root = null) => {
  const key = getObserverKey(rootMargin, threshold, root);

  if (!observersMap.has(key)) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const callbacks = observer._callbacks.get(entry.target);
          if (callbacks) {
            callbacks.forEach((callback) => callback(entry));
          }
        });
      },
      { rootMargin, threshold, root },
    );

    observer._callbacks = new Map();
    observersMap.set(key, observer);
  }

  return observersMap.get(key);
};

/**
 * High-performance Intersection Observer hook with memory optimization and request coalescing.
 * Efficiently tracks element visibility with shared observer instances and automatic cleanup.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.rootMargin - Margin around root element (CSS margin syntax) - default: '0px'
 * @param {number} options.threshold - Visibility threshold (0-1) to trigger intersection - default: 0
 * @param {Element|null} options.root - Root element for intersection (null = viewport) - default: null
 * @param {boolean} options.once - Whether to observe only once and auto-cleanup - default: false
 * @param {boolean} options.enabled - Whether observer is active - default: true
 */
const useIntersectionObserver = ({
  rootMargin = '0px',
  threshold = 0,
  root = null,
  once = false,
  enabled = true,
} = {}) => {
  const elementRef = useRef(null);
  const [entry, setEntry] = useState(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const observedOnce = useRef(false);

  useEffect(() => {
    const element = elementRef.current;

    if (!element || !enabled) return;
    if (once && observedOnce.current) return;

    const observer = getObserver(rootMargin, threshold, root);
    const callback = (intersectionEntry) => {
      setEntry(intersectionEntry);
      setIsIntersecting(intersectionEntry.isIntersecting);

      if (once && intersectionEntry.isIntersecting) {
        observedOnce.current = true;
        observer.unobserve(element);
        observer._callbacks.delete(element);
      }
    };

    if (!observer._callbacks.has(element)) {
      observer._callbacks.set(element, new Set());
    }

    observer._callbacks.get(element).add(callback);
    observer.observe(element);

    return () => {
      if (observer._callbacks.has(element)) {
        observer._callbacks.get(element).delete(callback);
        if (observer._callbacks.get(element).size === 0) {
          observer._callbacks.delete(element);
          observer.unobserve(element);
        }
      }
    };
  }, [rootMargin, threshold, root, once, enabled]);

  return {
    entry,
    ref: elementRef,
    isVisible: isIntersecting,
    isIntersecting,
  };
};

export default useIntersectionObserver;
